import BodyContainer from "../components/BodyContainer";
import {
    Button,
    Dialog, DialogActions,
    DialogContent,
    DialogSurface, DialogTitle,
    DialogTrigger,
    Input,
    Label,
    Title1
} from "@fluentui/react-components";
import ProgressRing from "../components/ProgressRing";
import {useEffect, useId, useState} from "react";
import {fetchClubIDFromDatabase} from "../handlers/databasehandler";
import Club from "../models/Club";
import MessageBox from "../components/MessageBar";
import {getClubData, updateClubData} from "../handlers/clubhandler";
import SettingsItem from "../components/SettingsItem";
import {createNewClub, deleteUser, getLoggedInUser, getUserID, signOutUser} from "../handlers/userhandler";
import {navigateToPage} from "../handlers/routehandler";
import LoginCard from "../components/LoginCard";

const ChangeAccount = ({ isNew = false }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [clubData, setClubData] = useState(new Club("","","",""));
    const [clubId, setClubId] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const [errorMsgType, setErrorMsgType] = useState("error");

    const [club, setClub] = useState("");
    const [shortClub, setShortClub] = useState("");
    const [contact, setContact] = useState("");
    const [phone, setPhone] = useState("");
    const [address, setAddress] = useState("");
    const [mail, setMail] = useState("");

    const clubNameId = useId();
    const shortClubId = useId();
    const contactId = useId();
    const phoneId = useId();
    const addressId = useId();
    const emailId = useId();

    useEffect(() => {
        const fetchData = async () => {
            const clubIdResult = await fetchClubIDFromDatabase();

            if (!clubIdResult || !clubIdResult.id) {
                setErrorMsgType("error");
                setErrorMsg("Failed to fetch account information");
                setIsLoading(false);
                return;
            }

            setClubId(clubIdResult.id);

            const clubDataResult = await getClubData(clubIdResult.id);

            if (!clubDataResult) {
                setErrorMsgType("error");
                setErrorMsg("Failed to fetch club data");
                setIsLoading(false);
                return;
            }

            setClubData(clubDataResult);
            setClub(clubDataResult.club || "");
            setShortClub(clubDataResult.shortClub || "");
            setContact(clubDataResult.contact || "");
            setPhone(clubDataResult.phone || "");
            setMail(clubDataResult.mail || "");
            setAddress(clubDataResult.address || "");
            setIsLoading(false);
        };

        if (isNew) {
            setIsLoading(false);

            setMail(getLoggedInUser());

            return;
        }

        fetchData().then(r => {});

    }, []);

    const deleteAccount = async () => {
        setIsLoading(true);
        if (!await deleteUser(await getUserID())) {
            setErrorMsgType("error");
            setErrorMsg("Failed to delete user");
            setIsLoading(false);
            return;
        }
        await signOutUser();

        setErrorMsgType("success");
        setErrorMsg("Account successfully deleted. It may take one month before the data will be deleted.");
        setIsLoading(false);

        setTimeout(() => {
            window.location.reload();
        }, 3000);
    };

    const saveAccount = async () => {
        setIsLoading(true);

        if (await updateClubData(clubId, club, shortClub, contact, phone, address, mail) == null) {
            setErrorMsgType("error");
            setErrorMsg("Failed to update club data");
            setIsLoading(false);
            return;
        }

        setErrorMsgType("success");
        setErrorMsg("Data successfully updated!");
        setIsLoading(false);
    };

    const createAccount = async () => {
        setIsLoading(true);

        const result = await createNewClub(await getUserID(), mail, club, shortClub, contact, phone, address);

        setIsLoading(false);

        if (result != null) {
            setErrorMsgType("error");
            setErrorMsg(result);
            return;
        }

        navigateToPage(``)
    }

    return (
        <BodyContainer showBackButton={!isNew} content={
            <>
                {errorMsg != null ? (
                    <MessageBox
                        intent={errorMsgType}
                        title={errorMsgType === "error" ? "An error occurred:" : ""}
                        message={errorMsg}
                        closeButtonFunction={() => { setErrorMsg(null); }}
                    />
                ) : null}
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <Title1>{ isNew ? `Verenigingsgegevens maken` : `Verenigingsgegevens`}</Title1>
                </div>
                {isLoading && <ProgressRing />}
                {clubData != null && !isLoading && (errorMsg == null || isNew || errorMsgType === "success") && (
                    <>
                        <LoginCard
                            buttonText={"Opslaan"}
                            onClick={isNew ? createAccount : saveAccount}
                            content={
                                <>
                                    {isLoading ? <ProgressRing /> : null}
                                    <Label htmlFor={clubNameId}>Verenigingsnaam</Label>
                                    <Input
                                        id={clubNameId}
                                        value={club}
                                        onChange={e => setClub(e.target.value)}
                                    />
                                    <p className={`text-small text-sub text-no-margin`}>Wanneer dit een persoonlijk account is, voer dan de persoonlijke naam in</p>
                                    <Label htmlFor={shortClubId}>Afk. Club</Label>
                                    <Input
                                        id={shortClubId}
                                        value={shortClub}
                                        onChange={e => setShortClub(e.target.value)}
                                    />
                                    <Label htmlFor={contactId}>Contactpersoon</Label>
                                    <Input
                                        id={contactId}
                                        value={contact}
                                        onChange={e => setContact(e.target.value)}
                                    />
                                    <Label htmlFor={phoneId}>Telefoonnummer</Label>
                                    <Input
                                        id={phoneId}
                                        value={phone}
                                        onChange={e => setPhone(e.target.value)}
                                    />
                                    <Label htmlFor={addressId}>Adres</Label>
                                    <Input
                                        id={addressId}
                                        value={address}
                                        onChange={e => setAddress(e.target.value)}
                                    />
                                    <Label htmlFor={emailId}>Email</Label>
                                    <Input
                                        id={emailId}
                                        value={mail}
                                        type={`email`}
                                        onChange={e => setMail(e.target.value)}
                                    />
                                    <div>
                                        <p className={`text-no-margin text-small`}>Deze email wordt gebruikt voor eventuele wedstrijdinformatie.</p>
                                        <p className={`text-no-margin text-small`}>Voor het inloggen wordt de volgende email gebruikt: { getLoggedInUser() }. Dit kan niet worden gewijzigd.</p>
                                    </div>
                                    <p className={`text-no-margin text-small`}>Door de verenigingsgegevens op te slaan, verklaart u akkoord te gaan dat de ingevoerde informatie mag worden gebruikt voor de organisatie van wedstrijden en voor het controleren van het recht op deelname volgens de reglementen van het WSV.</p>
                                    <a className={`text-no-margin text-small`} href={`#/privacy`}>Privacyverklaring</a>
                                </>
                            }
                        />
                        { !isNew &&
                            <Dialog>
                                <DialogTrigger>
                                    <SettingsItem
                                        title={"Account verwijderen"}
                                        text={"Hiermee alle gegevens en deelnemers verwijderd."}
                                        buttonText={"Verwijderen"}
                                        buttonColor={"red"}
                                    />
                                </DialogTrigger>
                                <DialogSurface className={`dialog`}>
                                    <DialogTitle>
                                        Account verwijderen
                                    </DialogTitle>
                                    <DialogContent>
                                        Wanneer het account wordt verwijderd, worden ook alle deelnemers en inschrijvingen van dit account verwijderd. Deze actie kan niet ongedaan worden gemaakt. Het kan maximaal 1 maand duren voordat de gegevens volledig worden verwijderd.
                                    </DialogContent>
                                    <DialogActions>
                                        <DialogTrigger>
                                            <Button as={"a"} className={`button`} appearance="secondary">Annuleren</Button>
                                        </DialogTrigger>
                                        <DialogTrigger>
                                            <Dialog>
                                                <DialogTrigger>
                                                    <Button
                                                        as={"a"}
                                                        className={`button`}
                                                        appearance="primary"
                                                        style={{ backgroundColor: "red" }}
                                                    >
                                                        Verwijderen
                                                    </Button>
                                                </DialogTrigger>
                                                <DialogSurface className={`dialog`}>
                                                    <DialogTitle>
                                                        Weet je het zeker?
                                                    </DialogTitle>
                                                    <DialogContent>
                                                        Weet je zeker dat je dit account wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                                                    </DialogContent>
                                                    <DialogActions>
                                                        <DialogTrigger>
                                                            <Button as={"a"} className={`button`} appearance="secondary">Annuleren</Button>
                                                        </DialogTrigger>
                                                        <DialogTrigger>
                                                            <Button
                                                                as={"a"}
                                                                className={`button`}
                                                                appearance="primary"
                                                                style={{ backgroundColor: "red" }}
                                                                onClick={async () => { await deleteAccount(); }}
                                                            >
                                                                Verwijderen
                                                            </Button>
                                                        </DialogTrigger>
                                                    </DialogActions>
                                                </DialogSurface>
                                            </Dialog>
                                        </DialogTrigger>
                                    </DialogActions>
                                </DialogSurface>
                            </Dialog>
                        }
                    </>
                )}
            </>
        } />
    );
};

export default ChangeAccount;
